import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { BUILD_SHA } from '@shared/api/config';
import { installErrorReporting, installWebVitals } from '@shared/telemetry';

import { App } from './App';
import './styles/global.css';

/**
 * The ONLY DOM mount in the app.
 *
 * It does NOT set `lang`, `dir`, or the theme attribute: `index.html` owns all
 * three, and writing them from JavaScript re-introduces exactly the first-paint
 * flicker that authoring them in the document exists to prevent.
 */
window.__BUILD_SHA__ = BUILD_SHA;

installErrorReporting();
installWebVitals();

async function bootstrap(): Promise<void> {
  // Compile-time branch: Vite replaces `import.meta.env.DEV` with a literal, so
  // this whole block — the worker, the fixtures, and the synthetic session — is
  // dead-code eliminated from a production build. It is deliberately not a
  // `VITE_*` flag: a VITE value is readable at runtime, so a bypass behind one
  // would ship the bypass.
  if (import.meta.env.DEV) {
    const [{ startWorker }, { seedDevSession }] = await Promise.all([
      import('./mocks/browser'),
      import('@shared/auth/dev-session'),
    ]);
    // Awaited BEFORE the mount, so no real request escapes during startup.
    await startWorker();
    seedDevSession();
  }

  const container = document.getElementById('root');
  if (!container) throw new Error('#root is missing from index.html');

  createRoot(container).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

void bootstrap();
